/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-prototype-builtins */
import { Injectable, ViewChild } from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { DataFormatterService } from 'src/app/common/data-formatter.service';


Chart.register(...registerables);
Chart.register(ChartDataLabels);


@Injectable({
  providedIn: 'root'
})
export class ChartService {
  chartType: 'bar' | 'line' | 'pie' = 'bar';
  @ViewChild('chartCanvas') chartCanvas: any;
  chart: any;
  xLabels: any;
  pluginConfig: any;
  maxLabels: number = 20;

  chartMetrics: any;
  chartTitle: string = ""
  dataSets: any;
  labelSkip: boolean = true;
  xaxisDisplayed: boolean = false;
  yaxisDisplayed: boolean = false;
  xaxisRangeDisabled: boolean = false;
  customGraph: boolean = false;

  data: any[] = [];
  chartDimensions: any[] = [];
  graphDataSets: any = [];
  filterResultData: any = [];

  colorArray: string[] = [
    'rgba(248, 146, 41, 0.5)',
    '#718096',
    'rgba(255, 99, 132, 1)',

    'rgba(75, 192, 192, 1)',

    'rgba(54, 162, 235, 1)',
    'rgba(255, 159, 64, 1)',
    'rgba(248, 146, 41, 0.5)',
    'rgba(153, 102, 255, 1)',
  ];

  constructor(private formatterService: DataFormatterService) { }

  generateDynamicLabels(data: any[], stringKeys: string[]): string[] {

    return data.map(item => {
      const labelParts: string[] = [];
      stringKeys.forEach(key => {
        if (item[key]) {
          labelParts.push(item[key]);
        }
      });

      if (labelParts.length === 0) {
        return 'Unknown';
      }

      return labelParts.join(' - ');
    });
  }

  createDynamicDatasets(metrics: string[], data: any[]) {

    return metrics.map((metric, index) => ({
      label: metric.replace(/_/g, ' ').toUpperCase(),
      data: data.map((entry) => entry[metric]),
      backgroundColor: this.chartType === 'bar' ? this.colorArray[index] : this.chartType === 'pie' ? this.colorArray : undefined,
      borderColor: this.chartType === 'line' ? this.colorArray[index] : undefined,
      borderWidth: this.chartType === 'line' ? 2 : undefined,
    }));
  }

  chartInitialization(data: any, metaData: any) {
    this.customGraph = false;
    this.chartDimensions = this.extractValuesAfterDot(metaData['dimensions']);
    this.chartType = this.chartType = metaData['chart_data']['type'];
    this.chartTitle = metaData['chart_data']['title'];
    this.chartMetrics = metaData['metrics'];
    this.data = this.formatterService.processPercentageFields(data);
    this.xLabels = this.generateDynamicLabels(data, this.chartDimensions);
    this.xaxisRangeDisabled = this.xLabels.length <= 20 ? true : false
    this.dataSets = this.createDynamicDatasets(this.chartMetrics, data);
    this.graphDataSets = this.dataSets
    this.labelSkip = true;
    this.xaxisDisplayed = false
    this.yaxisDisplayed = true
    this.maxLabels = 20;
    const { numberKeys, numberValues } = this.separateData(this.data);
    if (numberKeys.length === 2 && metaData['dimensions'] === 'year' && numberValues.some(value => typeof value === 'number' && value < 0)) {
      this.chartType = 'line';
    }
    if (this.data.length == 1 && Object.keys(this.data[0]).length == 2) {
      this.chartType = 'bar';
      this.customGraph = true;
    }
  }

  createChart(data: any, metaData: any, chartCanvas: any,) {
    if (chartCanvas && chartCanvas.nativeElement) {
      this.chartInitialization(data, metaData)
      const pluginsConfig: any = {
        title: {
          display: true,
          text: this.chartTitle,
          font: {
            weight: 'bold',
            size: 12
          },
          color: 'black',
        },

      };
      const plugin = {
        id: 'customCanvasBackgroundColor',
        beforeDraw: (chartCanvas: any, args: any, options: { color: string; }) => {
          const { ctx } = chartCanvas;
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = options.color || '#99ffff';
          ctx.fillRect(0, 0, chartCanvas.width, chartCanvas.height);
          ctx.restore();
        }
      };
      pluginsConfig.legend = {
        display: this.customGraph ? false : true,
      };
      pluginsConfig.customCanvasBackgroundColor = {
        color: 'white',
      };

      if (this.xLabels.length <= 4 || (this.xLabels.length <= 10 && metaData['metrics'].length <= 2)) {
        pluginsConfig.datalabels = {
          anchor: this.customGraph ? "center" : "end",
          align: this.customGraph ? "center" : 'end',
          font: {
            weight: 'bold',
            size: this.customGraph ? 20 : 10,
            family: 'Plus Jakarta Sans',
          },
          color: 'black',
          formatter: (value: any) => value,

        };


      }
      else {
        Chart.unregister(ChartDataLabels);
      }

      const chartConfig: ChartConfiguration<'bar' | 'line' | 'pie', number[], string> = {
        type: this.customGraph ? 'bar' : this.chartType,
        data: {
          labels: this.xLabels,
          datasets: this.dataSets
        },
        options: {
          scales: {
            x: {
              display: this.chartType == 'pie' || this.xaxisDisplayed ? false : true,
              grid: {
                offset: true,
                drawOnChartArea: false
              },
              ticks: {
                autoSkip: this.labelSkip,
                maxTicksLimit: this.maxLabels,
                maxRotation: 90,
                minRotation: 45,
                display: this.xaxisDisplayed ? false : true
              }
            },
            y: {
              display: this.chartType == 'pie' || !this.yaxisDisplayed || this.customGraph ? false : true,
              reverse: false,
              grid: {
                offset: false,
                drawOnChartArea: false
              },
              min: undefined,
              max: undefined

            }
          },

          plugins: pluginsConfig,

        },
        plugins: [plugin],
      };
      this.pluginConfig = pluginsConfig
      this.chart = new Chart(chartCanvas.nativeElement, chartConfig);
      return this.chart

    } else {
      console.error('Chart canvas element is not available.');
    }
  }

  extractValuesAfterDot(arr: any) {

    const result = arr.map((item: string) => {
      const parts = item.split('.');

      return parts.length > 1 ? parts[1] : item;
    });


    return result;
  }

  updateChart(updatedLabels: any, updatedDatasets: any, chart: any,) {
    chart.data.labels = updatedLabels;
    chart.data.datasets = updatedDatasets;
    chart.update();
  }
  updateFilterData(data: any) {
    const existingIndex = this.filterResultData.findIndex((item: { resultIndex: any; }) => item.resultIndex === data.resultIndex);
    if (existingIndex == -1) {
      this.filterResultData.push(data)
    }
    else if (this.filterResultData.length !== 0) {
      this.filterResultData[existingIndex] = data
    }
    

  }
  resetFilterData(resultIndex: any) {
    this.filterResultData = this.filterResultData.filter((item: { resultIndex: any; }) => {
      return item.resultIndex !== resultIndex
    })
  }
  separateData(data: any[]) {
    const stringValues: string[] = [];
    const numberValues: (number | string)[] = [];
    const numberKeys: string[] = [];
    const stringKeys: string[] = [];

    data.forEach(item => {
      for (const key in item) {
        if (item.hasOwnProperty(key)) {
          const value = item[key];

          if (typeof value === 'string' && value.endsWith('%')) {
            const percentageValue = parseFloat(value.replace('%', ''));

            if (!isNaN(percentageValue)) {
              numberValues.push(value);
              if (!numberKeys.includes(key)) {
                numberKeys.push(key);
              }
            } else {
              stringValues.push(value);
            }
          } else {
            const convertedValue = Number(value);

            if (!isNaN(convertedValue)) {
              numberValues.push(convertedValue);
              if (!numberKeys.includes(key)) {
                numberKeys.push(key);
              }
            } else {
              stringValues.push(value);
              if (!stringKeys.includes(key)) {
                stringKeys.push(key);
              }
            }
          }
        }
      }
    });

    const uniqueStringValues = Array.from(new Set(stringValues));
    const uniqueStringKeys = Array.from(new Set(stringKeys));

    return {
      stringValues: uniqueStringValues,
      numberValues,
      numberKeys: Array.from(new Set(numberKeys)),
      stringKeys: uniqueStringKeys
    };
  }
}
